import { getLayoutData, getPageData, getPages } from "@/pages/api/api"
import {
	SEE_ALSO_INFO_BLOCK_QUERY,
	SINGLE_PAGE_QUERY,
	WHAT_WE_DO_CATEGORY_QUERY,
	WHAT_WE_DO_SINGLE_ACTION_QUERY,
} from "@/pages/api/fragments"
import useSetBannerData from "@/hooks/useSetBannerData"
import ExtendedContent from "@/components/common/ExtendedContent"
import { CONTENT_TYPES, PAGES, SINGLE_PAGE_TYPES } from "@/constants"
import SeeAlso, { getSeeAlsoData } from "@/components/SeeAlso"
import { useMemo } from "react"
import { getPathsQueryString } from "@/utils/utils"

export default function Page({ data }) {
	const bannerData = useSetBannerData(data)
	const content = data?.content || []
	const seeAlsoInfo = data?.seeAlsoInfo || { header: "", variant: "" }
	const formattedSeeAlsoData = useMemo(() => getSeeAlsoData(data), [data])

	return (
		<>
			<ExtendedContent content={content} globalBannerData={bannerData} />
			<SeeAlso header={seeAlsoInfo.header} variant={seeAlsoInfo.variant} data={formattedSeeAlsoData} />
		</>
	)
}

const EXCLUDE_PATHS = [PAGES.HOME, PAGES.OUR_EVENTS, PAGES.I_PAY, PAGES.CONTACT, PAGES.POLISH_RED_CROSS]

const pathsQuery = `
	query GetSinglePages($locale: SiteLocale) {
		allSinglePages(locale: $locale, filter: {slug: {notIn: ${getPathsQueryString(EXCLUDE_PATHS)} }, pageType: {eq: "${SINGLE_PAGE_TYPES.POLISH_RED_CROSS}"}}) {
			pageType
            singlePageName
            slug
		}
	}
`

const propsQuery = `
	query GetSinglePagesData($slug: String, $locale: SiteLocale)  {
		allSinglePages(locale: $locale, filter: {slug: {eq: $slug}}) {
			__typename
            ${SINGLE_PAGE_QUERY}
			seeAlsoInfo{
				${SEE_ALSO_INFO_BLOCK_QUERY}
			}
			seeAlsoLinks {
				__typename
				... on ${CONTENT_TYPES.WHAT_WE_DO_SINGLE_ACTION} {
					${WHAT_WE_DO_SINGLE_ACTION_QUERY}
				}
				... on ${CONTENT_TYPES.ALL_NEWS_PAGE} {
					title
					slug
					card {
						header
						description
					}
				}
				... on ${CONTENT_TYPES.WHAT_WE_DO_CATEGORY} {
					${WHAT_WE_DO_CATEGORY_QUERY}
				}
				... on ${CONTENT_TYPES.SINGLE_PAGE} {
					__typename
					id
					pageType
					slug
					card {
						header
						description
					}
				}
		    }
        }
    }
`

export async function getStaticProps({ params, locale }) {
	const { slug } = params
	const data = await getPageData("allSinglePages", slug, propsQuery, locale)
	const layout = await getLayoutData(locale)
	return {
		props: {
			data,
			locale,
			...layout,
			isDarkSearch: false,
		},
		revalidate: 60 * 2,
	}
}

export async function getStaticPaths({ locales }) {
	const paths = []
	for (const locale of locales) {
		const singlePages = await getPages("allSinglePages", pathsQuery, locale, "prc")
		const single = singlePages
			.map((page) => {
				return {
					params: {
						slug: page.slug,
					},
					locale,
				}
			})
			.filter((el) => {
				return el !== null
			})
		paths.push(...single)
	}
	return {
		paths,
		fallback: false,
	}
}
